/**
 * Textos e listas da aba Vendas assinadas (e do selo na fila), em português
 * de marcenaria. Puro (sem React): as regras ficam em
 * `utils/capacidade/vendasAssinadas.js`; aqui só se decide como cada venda
 * aparece e quais pedidos o PCP pode escolher ao ligar.
 */
import {
  CLASSE,
  ESTADO_VENDA,
  normalizarCliente,
  normalizarPedido,
  passouDoAvisoDeLigacao,
  pedidosDaPlanilha,
} from "../../utils/capacidade";
import { plural } from "../../utils/dinheiro";
import { dataCompleta, ddmm } from "./apresentacao";

export const ABA_VENDAS = "vendas";

/** O selo que acompanha a venda em toda lista da fila. */
export const SELO_VENDA_ASSINADA = "venda assinada, fora da planilha";

/** Quantos pedidos o seletor do Ligar mostra de uma vez (a busca filtra o resto). */
export const LIMITE_DE_PEDIDOS_NA_LISTA = 30;

const CLASSE_PADRAO = CLASSE.NORMAL;

/** "3 ambientes (1 pesado, 1 leve)". */
export function textoDosAmbientes(ambientes) {
  const lista = ambientes || [];
  const fora = [CLASSE.PESADO, CLASSE.LEVE]
    .map((classe) => ({ classe, n: lista.filter((a) => a?.classe === classe).length }))
    .filter((c) => c.n > 0)
    .map((c) => `${c.n} ${c.n === 1 ? c.classe : `${c.classe}s`}`);
  const base = plural(lista.length, "ambiente", "ambientes");
  return fora.length ? `${base} (${fora.join(", ")})` : base;
}

/** "Cozinha · Lavabo · Ambiente 3". */
export function nomesDosAmbientes(ambientes) {
  return (ambientes || []).map((a, i) => a?.nome || `Ambiente ${i + 1}`).join(" · ");
}

/** "registrada por Ana há 6 dias" (ou "hoje"). */
export function textoDaEspera(venda) {
  const dias = Number(venda?.dias_aguardando) || 0;
  const quando = dias === 0 ? "hoje" : `há ${plural(dias, "dia", "dias")}`;
  return venda?.registrada_por ? `registrada por ${venda.registrada_por} ${quando}` : `registrada ${quando}`;
}

/**
 * Situação da venda na lista: `{texto, tom}`. UM sinal de cor por linha, e
 * só quando pede ação: mais de 30 dias sem ligação (decisão 11).
 */
export function situacaoDaVenda(venda) {
  if (passouDoAvisoDeLigacao(venda)) return { texto: "mais de 30 dias sem ligar", tom: "warning" };
  return { texto: "aguardando o PCP", tom: null };
}

/**
 * Por que o pedido não pode ser ligado, ou `null`. Mesma régua de
 * identidade do histórico de comentários (`indiceDeIdentidade`): pedido com
 * dois clientes (número reaproveitado) ou ambiente repetido no pedido
 * (linha duplicada) — a fila da planilha não conta esse pedido direito, e a
 * venda sairia da fila no lugar errado.
 */
export function bloqueioDaLigacao(pedidoNormalizado, indice) {
  if (!indice || !pedidoNormalizado) return null;
  if (indice.pedidosAmbiguos?.has(pedidoNormalizado)) {
    const item = (indice.pedidosComVariosClientes || []).find((p) => p.pedidoNormalizado === pedidoNormalizado);
    return {
      motivo: "pedido_com_varios_clientes",
      mensagem:
        `O pedido ${item?.pedido || pedidoNormalizado} aparece na planilha para mais de um cliente ` +
        `(${(item?.clientes || []).join(", ")}). Corrija o número na planilha antes de ligar.`,
    };
  }
  const repetido = (indice.ambientesRepetidos || []).find((a) => a.chave.split(":")[0] === pedidoNormalizado);
  if (repetido) {
    return {
      motivo: "ambiente_repetido",
      mensagem:
        `O pedido tem mais de uma linha com o ambiente "${repetido.rotulo}". Diferencie os nomes na planilha ` +
        "antes de ligar: hoje a fila conta só uma delas.",
    };
  }
  return null;
}

function contem(texto, busca) {
  return String(texto || "").includes(busca);
}

/**
 * Pedidos que o PCP pode escolher ao ligar a venda: `[{...pedido, sugerido,
 * bloqueio}]`. Os sugeridos (mesmo cliente, venda da época) vêm primeiro;
 * depois o mesmo cliente; depois os mais recentes. `busca` filtra por número
 * ou cliente; a lista para em `LIMITE_DE_PEDIDOS_NA_LISTA`.
 */
export function pedidosParaLigar({ linhas, venda, sugeridos = [], indice, busca = "" }) {
  const sugeridosSet = new Set(sugeridos.map((p) => p.pedidoNormalizado));
  const clienteDaVenda = normalizarCliente(venda?.cliente);
  const termo = normalizarCliente(busca);
  const pedidos = pedidosDaPlanilha(linhas)
    .filter((p) => !termo || contem(p.pedidoNormalizado, normalizarPedido(busca)) || contem(p.clienteNormalizado, termo))
    .map((p) => ({
      ...p,
      sugerido: sugeridosSet.has(p.pedidoNormalizado),
      mesmoCliente: Boolean(clienteDaVenda) && p.clienteNormalizado === clienteDaVenda,
      bloqueio: bloqueioDaLigacao(p.pedidoNormalizado, indice),
    }))
    .sort(
      (a, b) =>
        Number(b.sugerido) - Number(a.sugerido) ||
        Number(b.mesmoCliente) - Number(a.mesmoCliente) ||
        String(b.dataVenda || "").localeCompare(String(a.dataVenda || "")) ||
        a.pedido.localeCompare(b.pedido, "pt-BR", { numeric: true }),
    );
  return { pedidos: pedidos.slice(0, LIMITE_DE_PEDIDOS_NA_LISTA), total: pedidos.length };
}

/** "720 · MARIA SOUZA (1 ambiente, venda 03/10)". */
export function rotuloDoPedidoDaPlanilha(p) {
  const venda = p.dataVenda ? `venda ${ddmm(p.dataVenda)}` : "sem data de venda";
  return `${p.pedido} · ${p.cliente || "—"} (${plural(p.ambientes, "ambiente", "ambientes")}, ${venda})`;
}

/**
 * A sugestão de ligação em uma frase, com o aviso de que a obra pode estar
 * contando duas vezes enquanto ninguém confirma.
 */
export function textoDaSugestao(pedidos) {
  if (!pedidos?.length) return null;
  const nomes = pedidos.map((p) => p.pedido);
  const quais =
    nomes.length === 1 ? `O pedido ${nomes[0]} da planilha parece` : `Os pedidos ${nomes.join(", ")} da planilha parecem`;
  return (
    `${quais} ser esta venda (mesmo cliente, vendido perto da assinatura). Enquanto o PCP não ligar, a mesma ` +
    "obra pode estar contando duas vezes na fila."
  );
}

/** Aviso do topo da página: quantas vendas parecem já estar na planilha. */
export function textoDoAvisoDeSugestoes(quantidade) {
  if (!quantidade) return null;
  const quantas =
    quantidade === 1
      ? "1 venda assinada parece já estar digitada na planilha"
      : `${quantidade} vendas assinadas parecem já estar digitadas na planilha`;
  return `${quantas}: até o PCP ligar, a mesma obra pode estar contando duas vezes na fila.`;
}

/** Formulário de registro em branco (assinatura hoje). */
export function formularioVazio(hoje) {
  return {
    cliente: "",
    dataAssinatura: hoje,
    entregaDesejada: "",
    ambientes: [{ nome: "", classe: CLASSE_PADRAO }],
    observacao: "",
  };
}

/** "Registrar como venda assinada" a partir da simulação: mesmos ambientes e data. */
export function formularioDaSimulacao(entrada, hoje) {
  if (!entrada) return formularioVazio(hoje);
  return {
    ...formularioVazio(hoje),
    entregaDesejada: entrada.entregaDesejada || "",
    ambientes: (entrada.ambientes || []).map((a) => ({ nome: "", classe: a.classe || CLASSE_PADRAO })),
  };
}

/** Linha da venda para o arquivo exportado. */
function linhaDoArquivo(venda) {
  return {
    cliente: venda.cliente,
    ambientes: textoDosAmbientes(venda.ambientes),
    entrega: venda.entrega_desejada,
    assinatura: venda.data_assinatura,
    espera: textoDaEspera(venda),
  };
}

/** Seção "Vendas assinadas" do arquivo exportado (só quando há alguma aguardando). */
export function secaoDasVendas(vendas) {
  const aguardando = (vendas || []).filter((v) => v.estado === ESTADO_VENDA.AGUARDANDO);
  if (aguardando.length === 0) return null;
  return {
    id: "vendas_assinadas",
    label: "Vendas assinadas aguardando o PCP (fora da planilha, já na fila)",
    columns: [
      { key: "cliente", label: "Cliente", type: "text" },
      { key: "ambientes", label: "Ambientes", type: "text" },
      { key: "entrega", label: "Entrega desejada", type: "date" },
      { key: "assinatura", label: "Assinatura", type: "date" },
      { key: "espera", label: "Aguardando", type: "text" },
    ],
    rows: aguardando.map(linhaDoArquivo),
  };
}

/** "Entrega desejada 20/11/2026 · assinada 01/10/2026". */
export function textoDasDatas(venda) {
  return `entrega desejada ${dataCompleta(venda.entrega_desejada)} · assinada ${dataCompleta(venda.data_assinatura)}`;
}
